import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import Nav from './components/Nav';
import Home from './components/Home';
import AboutUs from './components/AboutUs';
import Contact from './components/Contact';
import HittaKompetens from './components/hittakompetens';
import ServiceLanding from './components/ServiceLanding';
import EndSection from './components/EndSection';
import Footer from './components/Footer';
import logo from './images/Logo.png';
import {
  forceScrollWindowToTop,
  queueMicrotaskSafe,
  scrollToElementId,
  smoothScrollToElementId,
  smoothScrollWindowToTop
} from './utils/scrollWindow';
import { applyPageMeta } from './utils/seo';

const TEAMTAILOR_JOBS_URL = 'https://job.execora.se/';
const TEAMTAILOR_REDIRECT_DELAY_MS = 1800;
const TEAMTAILOR_REDIRECT_FLAG = 'execora_teamtailor_redirect';
const ABOUT_PATH = '/omoss';
const CONTACT_PATH = '/kontakt';
const COMPETENCE_PATH = '/kompetens';
const SERVICE_REK_PATH = '/rex';
const SERVICE_BEM_PATH = '/bem';
const SERVICE_INTERIM_PATH = '/interim';
const SERVICES_PATH = '/tjanster';

function normalizePathname(pathname) {
  if (!pathname) return '/';
  const trimmed = pathname.replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
}
const teamtailorRedirectText = {
  swe: {
    title: 'Nu lämnar du sidan',
    subtitle: 'Du skickas nu till våra lediga uppdrag på job.execora.se.'
  },
  eng: {
    title: 'You are leaving this site',
    subtitle: 'You are now being redirected to our open roles at job.execora.se.'
  }
};

function getPageFromPath(pathname) {
  const p = normalizePathname(pathname);
  if (p === ABOUT_PATH) return 'about';
  if (p === CONTACT_PATH) return 'contact';
  if (p === COMPETENCE_PATH) return 'competence';
  if (p === SERVICE_REK_PATH) return 'serviceRex';
  if (p === SERVICE_BEM_PATH) return 'serviceBem';
  if (p === SERVICE_INTERIM_PATH) return 'serviceInterim';
  if (p === SERVICES_PATH) return 'home';
  return 'home';
}

function App() {
  const [lang, setLang] = useState('swe');
  const [page, setPage] = useState(() => getPageFromPath(window.location.pathname));
  const [isRedirectingToJobs, setIsRedirectingToJobs] = useState(false);
  const redirectTimeoutRef = useRef(null);
  const pageRef = useRef(page);
  const scrollRafRef = useRef(0);

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  // Per-page SEO metadata (title, description, canonical, Open Graph).
  useEffect(() => {
    applyPageMeta(page, lang);
  }, [page, lang]);

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useLayoutEffect(() => {
    if (page === 'home') {
      const path = normalizePathname(window.location.pathname);
      if (path === SERVICES_PATH) {
        // Land directly at services (avoid visible jump from top on back/refresh).
        scrollToElementId('services', 'auto');
        return;
      }
    }
    forceScrollWindowToTop();
  }, [page]);

  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) {
        window.clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handlePageShow = () => {
      if (sessionStorage.getItem(TEAMTAILOR_REDIRECT_FLAG) === '1') {
        sessionStorage.removeItem(TEAMTAILOR_REDIRECT_FLAG);
        if (redirectTimeoutRef.current) {
          window.clearTimeout(redirectTimeoutRef.current);
          redirectTimeoutRef.current = null;
        }
        setIsRedirectingToJobs(false);
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  useEffect(() => {
    const onPopState = () => {
      setPage(getPageFromPath(window.location.pathname));
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // Keep URL in sync with scroll position on the home page:
  // - Hero/home section => "/"
  // - Services section  => "/tjanster"
  // Use replaceState to avoid spamming browser history while scrolling.
  useEffect(() => {
    if (page !== 'home') return;

    const servicesEl = document.getElementById('services');
    const homeEl = document.getElementById('home');
    if (!servicesEl || !homeEl) return;

    const setPath = (targetPath) => {
      const currentPath = normalizePathname(window.location.pathname);
      // Only auto-toggle between "/" and "/tjanster" while on the home page.
      if (currentPath !== '/' && currentPath !== SERVICES_PATH) return;
      if (currentPath === targetPath) return;
      window.history.replaceState({}, '', targetPath);
    };

    // Prefer IntersectionObserver (more reliable than scroll events in some setups).
    // When the services section intersects the "header area", switch to /tjanster.
    let observer = null;
    if (typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) return;
          if (entry.isIntersecting) setPath(SERVICES_PATH);
          else setPath('/');
        },
        {
          root: null,
          // Trigger when the top part of the section passes the header height.
          rootMargin: '-88px 0px -55% 0px',
          threshold: 0
        }
      );
      observer.observe(servicesEl);
    }

    // Fallback + keep it responsive on resize.
    const updatePathFromScroll = () => {
      scrollRafRef.current = 0;
      const headerOffset = 88;
      const rect = servicesEl.getBoundingClientRect();
      const active = rect.top <= headerOffset && rect.bottom > headerOffset + 80;
      setPath(active ? SERVICES_PATH : '/');
    };

    const onScrollOrResize = () => {
      if (scrollRafRef.current) return;
      scrollRafRef.current = requestAnimationFrame(updatePathFromScroll);
    };

    updatePathFromScroll();
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
      if (observer) observer.disconnect();
      if (scrollRafRef.current) {
        cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = 0;
      }
    };
  }, [page]);

  const redirectToTeamtailor = () => {
    if (isRedirectingToJobs) return;
    sessionStorage.setItem(TEAMTAILOR_REDIRECT_FLAG, '1');
    setIsRedirectingToJobs(true);
    redirectTimeoutRef.current = window.setTimeout(() => {
      window.location.assign(TEAMTAILOR_JOBS_URL);
    }, TEAMTAILOR_REDIRECT_DELAY_MS);
  };

  const navigateTo = (target) => {
    if (target === 'home') {
      setPage('home');
      if (window.location.pathname !== '/') {
        window.history.pushState({}, '', '/');
      }
      smoothScrollWindowToTop();
      return;
    }
    if (target === 'about') {
      setPage('about');
      if (window.location.pathname !== ABOUT_PATH) {
        window.history.pushState({}, '', ABOUT_PATH);
      }
      return;
    }
    if (target === 'contact') {
      setPage('contact');
      if (window.location.pathname !== CONTACT_PATH) {
        window.history.pushState({}, '', CONTACT_PATH);
      }
      return;
    }
    if (target === 'jobs') {
      redirectToTeamtailor();
      return;
    }
    if (target === 'competence') {
      setPage('competence');
      if (normalizePathname(window.location.pathname) !== COMPETENCE_PATH) {
        window.history.pushState({}, '', COMPETENCE_PATH);
      }
      return;
    }
    if (target === 'serviceRex') {
      setPage('serviceRex');
      if (normalizePathname(window.location.pathname) !== SERVICE_REK_PATH) {
        window.history.pushState({}, '', SERVICE_REK_PATH);
      }
      queueMicrotaskSafe(() => {
        requestAnimationFrame(() => {
          forceScrollWindowToTop();
        });
      });
      return;
    }
    if (target === 'serviceBem') {
      setPage('serviceBem');
      if (normalizePathname(window.location.pathname) !== SERVICE_BEM_PATH) {
        window.history.pushState({}, '', SERVICE_BEM_PATH);
      }
      queueMicrotaskSafe(() => {
        requestAnimationFrame(() => {
          forceScrollWindowToTop();
        });
      });
      return;
    }
    if (target === 'serviceInterim') {
      setPage('serviceInterim');
      if (normalizePathname(window.location.pathname) !== SERVICE_INTERIM_PATH) {
        window.history.pushState({}, '', SERVICE_INTERIM_PATH);
      }
      queueMicrotaskSafe(() => {
        requestAnimationFrame(() => {
          forceScrollWindowToTop();
        });
      });
      return;
    }
    if (target === '#services' || target === 'services') {
      setPage('home');
      window.history.pushState({}, '', SERVICES_PATH);
      const go = () => smoothScrollToElementId('services');
      queueMicrotaskSafe(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(go);
        });
      });
      return;
    }
  };

  if (isRedirectingToJobs) {
    const redirectText = teamtailorRedirectText[lang] || teamtailorRedirectText.swe;
    return (
      <div className="teamtailor-redirect-screen" role="status" aria-live="polite">
        <img src={logo} alt="Execora logo" className="teamtailor-redirect-logo" />
        <p className="teamtailor-redirect-title">{redirectText.title}</p>
        <p className="teamtailor-redirect-subtitle">{redirectText.subtitle}</p>
      </div>
    );
  }

  return (
    <div className="App">
      <Nav lang={lang} setLang={setLang} page={page} navigateTo={navigateTo} />
      <main>
        {page === 'about' && <AboutUs lang={lang} navigateTo={navigateTo} />}
        {page === 'contact' && <Contact lang={lang} />}
        {page === 'competence' && <HittaKompetens lang={lang} setPage={setPage} />}
        {page === 'serviceRex' && <ServiceLanding lang={lang} variant="rex" navigateTo={navigateTo} />}
        {page === 'serviceBem' && <ServiceLanding lang={lang} variant="bem" navigateTo={navigateTo} />}
        {page === 'serviceInterim' && <ServiceLanding lang={lang} variant="interim" navigateTo={navigateTo} />}
        {page === 'home' && <Home lang={lang} setPage={navigateTo} onRedirectToJobs={redirectToTeamtailor} />}
      </main>
      <EndSection lang={lang} />
      <Footer lang={lang} />
    </div>
  );
}

export default App;

